/**
 * Consent Forms List Component
 *
 * Displays available consent forms and handles form submission for parents
 */

import React, { useState } from 'react';
import { Card, Button, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import { BsFileText, BsCalendar, BsCheckCircle, BsClock } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import parentService from '../../services/parentService';

const ConsentFormsList = ({ forms, selectedChild, onSubmitSuccess }) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleViewForm = (form) => {
    // Navigate to form submission with the correct route format
    // Use: /form-submission/{formId}/new/{patientId}
    navigate(`/form-submission/${form.form_id}/new/${selectedChild.id}`, {
      state: {
        formId: form.form_id,
        patientId: selectedChild.id,
        patientName: `${selectedChild.demographics?.first_name} ${selectedChild.demographics?.last_name}`,
        isConsentForm: true,
        returnUrl: '/parent-dashboard'
      }
    });
  };

  const handleShowPreview = (form) => {
    setSelectedForm(form);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedForm(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <>
      <div className="consent-forms-list">
        {forms.map((form) => (
          <Card key={form.id} className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h5 className="mb-2">
                    <BsFileText className="me-2 text-primary" />
                    {form.name}
                  </h5>

                  {form.description && (
                    <p className="text-muted mb-2">{form.description}</p>
                  )}

                  <div className="form-metadata">
                    <small className="text-muted me-3">
                      <BsCalendar className="me-1" />
                      Created: {formatDate(form.created_at)}
                    </small>

                    {form.version && (
                      <Badge bg="secondary" className="me-2">
                        v{form.version}
                      </Badge>
                    )}

                    <Badge bg="success">
                      <BsCheckCircle className="me-1" />
                      Active
                    </Badge>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleShowPreview(form)}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleViewForm(form)}
                  >
                    Complete Form
                  </Button>
                </div>
              </div>

              {/* Show which child this is for */}
              <div className="mt-2">
                <small className="text-info">
                  <strong>For:</strong> {selectedChild.demographics?.first_name} {selectedChild.demographics?.last_name}
                </small>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      {/* Form Preview Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <BsFileText className="me-2" />
            {selectedForm?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedForm && (
            <>
              {selectedForm.description && (
                <Alert variant="info">
                  <strong>Description:</strong> {selectedForm.description}
                </Alert>
              )}

              <div className="mb-3">
                <h6>Form Details</h6>
                <ul className="list-unstyled">
                  <li>
                    <strong>Version:</strong> {selectedForm.version || '1.0'}
                  </li>
                  <li>
                    <strong>Created:</strong> {formatDate(selectedForm.created_at)}
                  </li>
                  <li>
                    <strong>Category:</strong> {selectedForm.category || 'Consent'}
                  </li>
                  <li>
                    <strong>Status:</strong>{' '}
                    <Badge bg="success">Active</Badge>
                  </li>
                </ul>
              </div>

              <Alert variant="warning">
                <BsClock className="me-2" />
                <strong>Note:</strong> This is a preview. Click "Complete Form" to fill out the actual consent form.
              </Alert>

              <div className="mt-3">
                <strong>Completing for:</strong>{' '}
                {selectedChild.demographics?.first_name} {selectedChild.demographics?.last_name}
                <br />
                <small className="text-muted">
                  MRN: {selectedChild.mrn_permanent || selectedChild.mrn_temporary}
                </small>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleCloseModal();
              handleViewForm(selectedForm);
            }}
          >
            Complete Form
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ConsentFormsList;
